const IMAGE_KEY_PATTERN = /(image|photo|picture|thumbnail|logo|media)/i;
const NON_SOURCE_KEY_PATTERN =
    /(alt|position|width|height|size|style|variant|opacity|overlay|prompt|caption|link|enabled|visible)$/i;
const IMAGE_SOURCE_PATTERN =
    /^(https?:\/\/|\/|data:image\/|blob:)|\.(avif|gif|jpe?g|png|svg|webp)(?:[?#].*)?$/i;
const VIDEO_SOURCE_PATTERN =
    /(youtube\.com|youtu\.be|vimeo\.com|facebook\.com\/.+video|\.(m3u8|mov|mp4|webm)(?:[?#].*)?$)/i;

const normalizeKey = (value) => String(value || '').toLowerCase();

function isImageSource(key, value) {
    return (
        typeof value === 'string' &&
        value.trim().length > 0 &&
        IMAGE_KEY_PATTERN.test(key) &&
        !NON_SOURCE_KEY_PATTERN.test(key) &&
        !VIDEO_SOURCE_PATTERN.test(value.trim()) &&
        IMAGE_SOURCE_PATTERN.test(value.trim())
    );
}

function findAltTextKey(record, imageKey) {
    const keys = Object.keys(record || {});
    const keyByNormalizedName = new Map(
        keys.map((key) => [normalizeKey(key), key])
    );
    const withoutLocation = imageKey.replace(/(url|src|source)$/i, '');
    const withoutImageWord = withoutLocation.replace(
        /(image|photo|picture|thumbnail|logo)$/i,
        ''
    );
    const candidates = [
        `${withoutLocation}AltText`,
        `${withoutImageWord}AltText`,
        'AltText',
        'altText',
        'alt_text',
        'alt',
    ];

    for (const candidate of candidates) {
        const matchingKey = keyByNormalizedName.get(normalizeKey(candidate));
        if (matchingKey) return matchingKey;
    }

    return null;
}

function findDecorativeKey(record, altKey) {
    const keys = Object.keys(record || {});
    const keyByNormalizedName = new Map(
        keys.map((key) => [normalizeKey(key), key])
    );
    const candidates = [`${altKey}Decorative`, `${altKey}_decorative`];

    for (const candidate of candidates) {
        const matchingKey = keyByNormalizedName.get(normalizeKey(candidate));
        if (matchingKey) return matchingKey;
    }

    return `${altKey}Decorative`;
}

function pathToEditorKey(path) {
    return path.reduce((result, segment) => {
        if (typeof segment === 'number') return `${result}[${segment}]`;
        return result ? `${result}.${segment}` : segment;
    }, '');
}

function imageLabel(url) {
    try {
        const pathname = new URL(url, 'https://editor.local').pathname;
        return (
            decodeURIComponent(pathname.split('/').filter(Boolean).pop()) ||
            'Site image'
        );
    } catch {
        return 'Site image';
    }
}

function collectUsedImages(components = []) {
    const imagesByUrl = new Map();

    const walk = (value, path, component) => {
        if (!value || typeof value !== 'object') return;

        if (Array.isArray(value)) {
            value.forEach((item, index) =>
                walk(item, [...path, index], component)
            );
            return;
        }

        Object.entries(value).forEach(([key, candidate]) => {
            if (!isImageSource(key, candidate)) return;
            const altKey = findAltTextKey(value, key);
            if (!altKey) return;

            const url = candidate.trim();
            const decorativeKey = findDecorativeKey(value, altKey);
            const id = url;
            const current = imagesByUrl.get(id) || {
                id,
                url,
                label: imageLabel(url),
                altText: '',
                usages: [],
            };
            const altText = String(value[altKey] || '').trim();
            const decorative = value[decorativeKey] === true;
            if (!current.altText && altText) current.altText = altText;
            current.usages.push({
                sourceId:
                    component.sourceId ||
                    `${component.pageName || 'current'}:${component.data_file}`,
                pageName: component.pageName || 'current',
                file: component.data_file,
                componentName: component.name,
                key: pathToEditorKey([...path, altKey]),
                path: [...path, altKey],
                imagePath: [...path, key],
                decorativePath: [...path, decorativeKey],
                altText,
                decorative,
            });
            imagesByUrl.set(id, current);
        });

        Object.entries(value).forEach(([key, child]) => {
            // Nav and Footer receive expanded company/brand objects for render
            // purposes. They are not part of their saved component JSON.
            if (path.length === 0 && ['company', 'brand'].includes(key)) return;
            if (child && typeof child === 'object') {
                walk(child, [...path, key], component);
            }
        });
    };

    components.forEach((component) => {
        if (!component?.data_file || !component?.props) return;
        walk(component.props, [], component);
    });

    return Array.from(imagesByUrl.values())
        .map((image) => ({
            ...image,
            decorative: image.usages.every((usage) => usage.decorative),
            missingAltText: image.usages.some(
                (usage) => !usage.altText && !usage.decorative
            ),
        }))
        .sort((a, b) => {
            if (a.missingAltText !== b.missingAltText) {
                return a.missingAltText ? -1 : 1;
            }
            return a.label.localeCompare(b.label);
        });
}

function setValueAtPath(target, path, value) {
    let current = target;
    for (const segment of path.slice(0, -1)) current = current[segment];
    current[path[path.length - 1]] = value;
}

function deleteValueAtPath(target, path) {
    let current = target;
    for (const segment of path.slice(0, -1)) current = current[segment];
    delete current[path[path.length - 1]];
}

const cloneContent = (value) =>
    typeof structuredClone === 'function'
        ? structuredClone(value)
        : JSON.parse(JSON.stringify(value));

function applyAltTextDrafts(
    sources = [],
    images = [],
    drafts = {},
    decorativeDrafts = {}
) {
    const nextSources = sources.map((source) => ({
        ...source,
        props: cloneContent(source.props),
        changed: false,
    }));
    const sourcesById = new Map(
        nextSources.map((source) => [source.id, source])
    );

    images.forEach((image) => {
        const decorative = Object.prototype.hasOwnProperty.call(
            decorativeDrafts,
            image.id
        )
            ? Boolean(decorativeDrafts[image.id])
            : Boolean(image.decorative);
        const value = decorative
            ? ''
            : String(drafts[image.id] ?? image.altText ?? '').trim();
        image.usages.forEach((usage) => {
            const source = sourcesById.get(usage.sourceId);
            if (!source || !Array.isArray(usage.path)) return;
            if (String(usage.altText || '').trim() !== value) {
                setValueAtPath(source.props, usage.path, value);
                source.changed = true;
            }
            if (
                Array.isArray(usage.decorativePath) &&
                Boolean(usage.decorative) !== decorative
            ) {
                if (decorative) {
                    setValueAtPath(source.props, usage.decorativePath, true);
                } else {
                    deleteValueAtPath(source.props, usage.decorativePath);
                }
                source.changed = true;
            }
        });
    });

    return nextSources;
}

function applyImageReplacements(sources = [], images = [], replacements = {}) {
    const nextSources = sources.map((source) => ({
        ...source,
        props: cloneContent(source.props),
        changed: Boolean(source.changed),
    }));
    const sourcesById = new Map(
        nextSources.map((source) => [source.id, source])
    );

    images.forEach((image) => {
        const replacementUrl = String(replacements[image.id]?.url || '').trim();
        if (!replacementUrl || replacementUrl === image.url) return;
        image.usages.forEach((usage) => {
            const source = sourcesById.get(usage.sourceId);
            if (!source || !Array.isArray(usage.imagePath)) return;
            setValueAtPath(source.props, usage.imagePath, replacementUrl);
            source.changed = true;
        });
    });

    return nextSources;
}

function applyImageDeletions(sources = [], images = [], deletedImageIds = []) {
    const nextSources = sources.map((source) => ({
        ...source,
        props: cloneContent(source.props),
        changed: Boolean(source.changed),
    }));
    const sourcesById = new Map(
        nextSources.map((source) => [source.id, source])
    );
    const deletedIds = new Set(deletedImageIds);

    images.forEach((image) => {
        if (!deletedIds.has(image.id)) return;

        image.usages.forEach((usage) => {
            const source = sourcesById.get(usage.sourceId);
            if (!source || !Array.isArray(usage.imagePath)) return;

            setValueAtPath(source.props, usage.imagePath, '');
            if (Array.isArray(usage.path)) {
                setValueAtPath(source.props, usage.path, '');
            }
            if (Array.isArray(usage.decorativePath)) {
                deleteValueAtPath(source.props, usage.decorativePath);
            }
            source.changed = true;
        });
    });

    return nextSources;
}

module.exports = {
    collectUsedImages,
    applyAltTextDrafts,
    applyImageReplacements,
    applyImageDeletions,
    findAltTextKey,
    pathToEditorKey,
};
